import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

const LandingPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [stats, setStats] = useState({ companies: 0, transactions: 0, savings: 0 });

  const testimonials = [
    {
      quote: "Rokda Hisab is the best digital bahi khata app I've used for my kirana shop. The Gujarati hisab book feature makes it so easy to track daily jama kharch and udhar jama records. Highly recommend for small shop owners!",
      author: "Rajesh Patel",
      position: "Kirana Shop Owner",
      company: "Small Business",
      rating: 5
    },
    {
      quote: "As a Gujarati vyapari, I needed a simple Gujarati hisab app for my shop. Rokda Hisab's free ledger app has everything - credit-debit ledger, customer reminders, and daily expense tracker. Best business accounting Gujarati app!",
      author: "Priya Shah",
      position: "Small Business Owner",
      company: "Retail",
      rating: 5
    },
    {
      quote: "The udhar jama record feature in Rokda Hisab is amazing. It's a perfect customer ledger app with reminders that helps me track all pending payments. The daily expense tracker Gujarati makes business accounting so simple.",
      author: "Amit Mehta",
      position: "Shopkeeper",
      company: "Retail Business",
      rating: 5
    }
  ];

  // Animated counter effect
  useEffect(() => {
    const animateCounters = () => {
      const targets = { companies: 2500, transactions: 150000, savings: 45 };
      const duration = 2000;
      const steps = 60;
      const stepDuration = duration / steps;

      let step = 0;
      const timer = setInterval(() => {
        step++;
        const progress = step / steps;
        setStats({
          companies: Math.floor(targets.companies * progress),
          transactions: Math.floor(targets.transactions * progress),
          savings: Math.floor(targets.savings * progress)
        });

        if (step >= steps) {
          clearInterval(timer);
          setStats(targets);
        }
      }, stepDuration);
    };

    const timer = setTimeout(animateCounters, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const features = [
    {
      icon: "📱",
      title: "Digital Bahi Khata",
      description: "Best free digital bahi khata app for small business. Maintain your hisab book digitally with credit-debit ledger in Gujarati language."
    },
    {
      icon: "💰",
      title: "Udhar Jama Record",
      description: "Perfect udhar jama app for kirana shops. Track customer credit-debit, set payment reminders, and manage daily jama kharch effortlessly."
    },
    {
      icon: "📊",
      title: "Daily Expense Tracker Gujarati",
      description: "Simple Gujarati hisab app for daily expense tracking. Track your business kharch and income in Gujarati language with detailed reports."
    },
    {
      icon: "🏪",
      title: "Kirana Shop Ledger",
      description: "Best free ledger app India for kirana shops. Manage customer udhar jama, daily jama kharch, and business accounting all in one place."
    },
    {
      icon: "🔔",
      title: "Customer Ledger with Reminders",
      description: "Customer ledger app with reminders for pending payments. Never miss a payment collection with automated udhar jama reminders."
    },
    {
      icon: "📝",
      title: "Business Accounting Gujarati",
      description: "Free business accounting Gujarati app for Gujarati vyapari. Complete business hisab book with reports and analytics in Gujarati language."
    }
  ];

  const solutions = [
    {
      title: "Simple Gujarati Hisab App for Small Shop",
      description: "Best free digital bahi khata for small shops. Easy kirana shop ledger with Gujarati language support for daily jama kharch tracking.",
      icon: "🏪",
      benefits: ["Gujarati hisab book", "Daily expense tracker", "Udhar jama record"]
    },
    {
      title: "Free Business Hisab Book Mobile App",
      description: "Complete business accounting Gujarati app. Track udhar jama, daily kharch, and generate business reports on mobile or desktop.",
      icon: "📱",
      benefits: ["Mobile & web access", "Offline support", "Cloud sync"]
    },
    {
      title: "Customer Ledger App with Reminders",
      description: "Perfect jama udhar record app with automatic payment reminders. Never forget pending payments with smart customer ledger management.",
      icon: "🔔",
      benefits: ["Auto reminders", "Payment tracking", "Customer reports"]
    }
  ];


  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <div className="logo-icon">💰</div>
            <h2>RokdaHisab</h2>
          </div>
          <div className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
            <a href="#features" className="nav-link">Features</a>
            <a href="#solutions" className="nav-link">Solutions</a>
            <a href="#testimonials" className="nav-link">Reviews</a>
            <a href="#contact" className="nav-link">Contact</a>
            <div className="nav-buttons">
              <Link to="/login" className="nav-link nav-signin">Sign In</Link>
              <Link to="/register" className="btn-primary">Get Started</Link>
            </div>
          </div>
          <div className="nav-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-background">
          <div className="floating-shapes" aria-label="Business finance icons">
            <div className="shape shape-1" aria-label="Credit card payment">💳</div>
            <div className="shape shape-2" aria-label="Mobile app">📱</div>
            <div className="shape shape-3" aria-label="Money and cash">💰</div>
            <div className="shape shape-4" aria-label="Business reports">📊</div>
          </div>
        </div>
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-icon" aria-label="Best App Badge">✨</span>
              <span>Best Free Digital Bahi Khata & Hisab Book App India</span>
            </div>
            <h1 className="hero-title">
              Rokda Hisab App - Digital Bahi Khata & 
              <span className="gradient-text"> Credit-Debit Ledger App</span>
            </h1>
            <p className="hero-subtitle">
              <strong>Best free Gujarati hisab book app</strong> for small business, kirana shop ledger, and daily expense tracker. 
              Simple digital bahi khata with udhar jama record keeping. Free business accounting app with customer reminders - 
              perfect for Gujarati vyapar hisab daily jama kharch management.
            </p>
            <div className="hero-buttons">
              <Link to="/register" className="btn-primary btn-large">
                <span>Get Started</span>
                <span className="btn-icon">→</span>
              </Link>
              <Link to="/login" className="btn-outline btn-large">
                <span>Sign In</span>
                <span className="btn-icon">▶</span>
              </Link>
            </div>
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-number">{stats.companies.toLocaleString()}+</div>
                <div className="stat-label">Companies</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{stats.transactions.toLocaleString()}+</div>
                <div className="stat-label">Transactions</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{stats.savings}%</div>
                <div className="stat-label">Cost Savings</div>
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="dashboard-preview">
              <div className="dashboard-header">
                <div className="dashboard-dots">
                  <span></span><span></span><span></span>
                </div>
                <div className="dashboard-title">RokdaHisab Dashboard</div>
              </div>
              <div className="dashboard-content">
                <div className="metric-card">
                  <div className="metric-icon" aria-label="Total cash flow">💰</div>
                  <div className="metric-value">2.4M</div>
                  <div className="metric-label">Total Cash Flow</div>
                </div>
                <div className="metric-card">
                  <div className="metric-icon" aria-label="Business growth">📈</div>
                  <div className="metric-value">+15%</div>
                  <div className="metric-label">Business Growth</div>
                </div>
                <div className="metric-card">
                  <div className="metric-icon" aria-label="App reliability">⚡</div>
                  <div className="metric-value">99.9%</div>
                  <div className="metric-label">Uptime</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <div className="section-header">
            <h2>Free Digital Bahi Khata & Hisab Book App Features</h2>
            <p>Best free ledger app India for small business, kirana shops, and Gujarati vyapari. Simple Gujarati hisab app with udhar jama record and daily expense tracking.</p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <div className="feature-arrow">→</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" className="solutions">
        <div className="container">
          <div className="section-header">
            <h2>Best Free Ledger App for Small Business & Kirana Shops</h2>
            <p>Perfect business accounting Gujarati app for kirana shop ledger, daily expense tracker, and customer udhar jama management</p>
          </div>
          <div className="solutions-grid">
            {solutions.map((solution, index) => (
              <div key={index} className="solution-card">
                <div className="solution-icon">{solution.icon}</div>
                <h3>{solution.title}</h3>
                <p>{solution.description}</p>
                <ul className="solution-benefits">
                  {solution.benefits.map((benefit, idx) => (
                    <li key={idx}>
                      <span className="check-icon">✓</span>
                      {benefit}
                    </li>
                  ))}
                </ul>
                <button className="btn-outline">Learn More</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Free & Simple Section */}
      <section id="free" className="free-section">
        <div className="container">
          <div className="section-header">
            <h2>Free & Simple Accounting</h2>
            <p>RokdaHisab is completely free to use for managing daily income and expenses. No subscriptions. No hidden charges.</p>
          </div>
          <div className="free-content">
            <div className="free-card">
              <div className="free-icon">✨</div>
              <h3>100% Free Forever</h3>
              <p>No credit card required. No hidden fees. Start managing your business finances today.</p>
            </div>
            <div className="free-card">
              <div className="free-icon">🚀</div>
              <h3>Full Features Included</h3>
              <p>All features are available to everyone. No premium tiers or limitations.</p>
            </div>
            <div className="free-card">
              <div className="free-icon">🔒</div>
              <h3>Your Data, Your Control</h3>
              <p>Your financial data stays private and secure. Export anytime, anywhere.</p>
            </div>
          </div>
          <div className="free-cta">
            <Link to="/register" className="btn-primary btn-large">
              <span>Get Started</span>
              <span className="btn-icon">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials">
        <div className="container">
          <div className="section-header">
            <h2>Loved by Small Business Owners & Gujarati Vyapari</h2>
            <p>See why thousands of shopkeepers trust Rokda Hisab for their digital bahi khata and business hisab book needs</p>
          </div>
          <div className="testimonial-container">
            <div className="testimonial-content">
              <div className="testimonial-quote">
                <div className="quote-icon">"</div>
                <p>{testimonials[currentTestimonial].quote}</p>
              </div>
              <div className="testimonial-author">
                <div className="author-info">
                  <h4>{testimonials[currentTestimonial].author}</h4>
                  <p>{testimonials[currentTestimonial].position}</p>
                  <div className="company-badge">{testimonials[currentTestimonial].company}</div>
                </div>
                <div className="rating">
                  {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                    <span key={i} className="star">⭐</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="testimonial-navigation">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  className={`nav-dot ${index === currentTestimonial ? 'active' : ''}`}
                  onClick={() => setCurrentTestimonial(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section for SEO */}
      <section id="faq" className="faq-section" style={{ padding: '80px 0', background: '#f7fafc' }}>
        <div className="container">
          <div className="section-header">
            <h2>Frequently Asked Questions - Digital Bahi Khata App</h2>
            <p>Common questions about Rokda Hisab - Best free Gujarati hisab book app</p>
          </div>
          <div className="faq-grid" style={{ maxWidth: '900px', margin: '0 auto', display: 'grid', gap: '20px' }}>
            <div className="faq-item" style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#1a1a1a' }}>What is Rokda Hisab app?</h3>
              <p style={{ color: '#4a5568', lineHeight: '1.7' }}>Rokda Hisab is a <strong>free digital bahi khata and credit-debit ledger app</strong> designed for small businesses, kirana shops, and shopkeepers in India. It helps you maintain your business hisab book digitally with udhar jama record keeping and daily expense tracking in Gujarati language.</p>
            </div>
            <div className="faq-item" style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#1a1a1a' }}>Is Rokda Hisab free ledger app India?</h3>
              <p style={{ color: '#4a5568', lineHeight: '1.7' }}>Yes, Rokda Hisab is <strong>completely free forever</strong>. It's the best free ledger app India for small businesses, kirana shops, and Gujarati vyapari. No hidden charges, no subscription fees - perfect for simple Gujarati hisab app for small shop owners.</p>
            </div>
            <div className="faq-item" style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#1a1a1a' }}>Can I use Rokda Hisab for kirana shop ledger?</h3>
              <p style={{ color: '#4a5568', lineHeight: '1.7' }}>Absolutely! Rokda Hisab is specifically designed for <strong>kirana shop ledger management</strong>. It's a simple Gujarati hisab app perfect for small shops with features like customer udhar jama record, daily kharch tracking, and business accounting in Gujarati.</p>
            </div>
            <div className="faq-item" style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#1a1a1a' }}>How to maintain udhar jama record in Rokda Hisab?</h3>
              <p style={{ color: '#4a5568', lineHeight: '1.7' }}>Rokda Hisab makes it easy to maintain <strong>udhar jama record</strong>. Simply add customer credit-debit entries, and the app automatically tracks all udhar jama transactions. You can set reminders for pending payments and generate reports for your business hisab book - perfect udhar jama record rakhnay wala app.</p>
            </div>
            <div className="faq-item" style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#1a1a1a' }}>Is there a Gujarati hisab book app available?</h3>
              <p style={{ color: '#4a5568', lineHeight: '1.7' }}>Yes, Rokda Hisab is a fully <strong>Gujarati hisab book app</strong> designed for Gujarati vyapari and shopkeepers. It supports Gujarati language for all features including bahi khata, daily jama kharch tracking, and business accounting - perfect for Gujarati vyapar hisab daily jama kharch.</p>
            </div>
            <div className="faq-item" style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#1a1a1a' }}>How to download free business hisab book mobile app?</h3>
              <p style={{ color: '#4a5568', lineHeight: '1.7' }}>Rokda Hisab is a web-based app that works on any device - mobile, tablet, or computer. Simply visit rokadahisab.com and register for free. No download required. It's the best <strong>free business hisab book mobile app</strong> for daily jama kharch tracking.</p>
            </div>
            <div className="faq-item" style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#1a1a1a' }}>Does the app send customer ledger reminders?</h3>
              <p style={{ color: '#4a5568', lineHeight: '1.7' }}>Yes, Rokda Hisab is a <strong>customer ledger app with reminders</strong>. You can set automatic reminders for pending udhar jama payments, helping you recover dues faster and maintain better business relationships with your customers.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Start Using Free Digital Bahi Khata App Today</h2>
            <p>Join thousands of kirana shops and small businesses using Rokda Hisab - the best free Gujarati hisab book app with udhar jama record and daily expense tracker</p>
            <div className="cta-buttons">
              <Link to="/register" className="btn-primary btn-large">
                <span>Get Started</span>
                <span className="btn-icon">→</span>
              </Link>
              <Link to="/login" className="btn-outline btn-large">
                <span>Sign In</span>
                <span className="btn-icon">📅</span>
              </Link>
            </div>
            <div className="cta-guarantee">
              <span className="guarantee-icon">🛡️</span>
              <span>Free forever • No credit card required • Start managing your business today</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-main">
              <div className="footer-brand">
                <div className="footer-logo">
                  <div className="logo-icon">💰</div>
                  <h3>RokdaHisab</h3>
                </div>
                <p>Best free digital bahi khata and credit-debit ledger app for small businesses in India. Simple Gujarati hisab book app for kirana shops with udhar jama record and daily expense tracking.</p>
                <div className="social-links">
                  <button type="button" className="social-link" aria-label="Email">📧</button>
                  <button type="button" className="social-link" aria-label="Twitter">🐦</button>
                  <button type="button" className="social-link" aria-label="LinkedIn">💼</button>
                  <button type="button" className="social-link" aria-label="Mobile">📱</button>
                </div>
              </div>
              <div className="footer-links">
                <div className="footer-column">
                  <h4>Product</h4>
                  <ul>
                    <li><a href="#features">Features</a></li>
                    <li><a href="/api-docs">API Documentation</a></li>
                    <li><a href="/integrations">Integrations</a></li>
                    <li><a href="/security">Security</a></li>
                  </ul>
                </div>
                <div className="footer-column">
                  <h4>Company</h4>
                  <ul>
                    <li><a href="/about">About Us</a></li>
                    <li><a href="/careers">Careers</a></li>
                    <li><a href="/press">Press</a></li>
                    <li><a href="/partners">Partners</a></li>
                    <li><a href="/contact">Contact</a></li>
                  </ul>
                </div>
                <div className="footer-column">
                  <h4>Resources</h4>
                  <ul>
                    <li><a href="/help">Help Center</a></li>
                    <li><a href="/blog">Blog</a></li>
                    <li><a href="/case-studies">Case Studies</a></li>
                    <li><a href="/webinars">Webinars</a></li>
                    <li><a href="/status">Status</a></li>
                  </ul>
                </div>
                <div className="footer-column">
                  <h4>Legal</h4>
                  <ul>
                    <li><a href="/privacy">Privacy Policy</a></li>
                    <li><a href="/terms">Terms of Service</a></li>
                    <li><a href="/cookies">Cookie Policy</a></li>
                    <li><a href="/gdpr">GDPR</a></li>
                    <li><a href="/compliance">Compliance</a></li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="footer-bottom">
              <div className="footer-bottom-content">
                <p>&copy; 2025 RokdaHisab. All rights reserved.</p>
                <div className="footer-certifications">
                  <span className="cert-badge">🔒 SOC 2 Certified</span>
                  <span className="cert-badge">🏦 RBI Compliant</span>
                  <span className="cert-badge">🔐 ISO 27001</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
